'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { useSearchParams } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';
import { SearchTopAdBanner } from '@/app/_components/ads';
import { usePageSearchBarVisibility, useSearchHistoryStore } from '@/app/_components/SearchBar';
import { DEFAULT_SEARCH_CATEGORY, isSearchCategory, SEARCH_CATEGORY_QUERY_KEY } from '@/app/_components/SearchBar/constants';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAppBack } from '@/modules/wello-app/_hooks/useAppBack';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { AllSearch } from './_components/AllSearch';
import { DonationBusinessSearch } from './_components/DonationBusinessSearch';
import { FestivalSearch } from './_components/FestivalSearch/FestivalSearch';
import { GiftSearch } from './_components/GiftSearch';
import { HometownNewsSearch } from './_components/HometownNewsSearch';
import { LocalMarketSearch } from './_components/LocalMarketSearch';
import { PolicyInfoSearch } from './_components/PolicyInfoSearch';
import { PolicySearch } from './_components/PolicySearch';
import { SearchPageHeader } from './_components/SearchPageHeader';
import { useIntegratedSearchFilter } from './_hooks/useIntegratedSearchFilter';
import { usePolicyFilter } from './_hooks/usePolicyFilter';
import { SECTION_HEADER_MAX_W } from './constants';
export default function SearchPage() {
  const $ = _c(76);
  useAppBack();
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const searchParams = useSearchParams();
  const resultsSectionRef = useRef(null);
  const {
    state,
    setKeyword,
    toggleTargetCode,
    toggleProvisionCode,
    toggleJobCode,
    toggleAgeCode,
    resetFilters,
    setSort,
    setPage,
    activeFilterCount
  } = usePolicyFilter();
  const {
    state: integratedState,
    setRegionCode,
    setSubRegionCode,
    togglePolicyInfoProvision,
    toggleGiftCategory,
    toggleGiftInstitution,
    toggleDonationInstitution,
    setDonationSort,
    setFestivalLclsSystm2,
    setFestivalRegionCode,
    setFestivalSubRegionCode,
    setFestivalRadius,
    setFestivalMonth,
    resetHometownNewsFilters,
    resetPolicyInfoFilters,
    resetGiftFilters,
    resetDonationFilters,
    resetFestivalFilters,
    hometownNewsFilterCount,
    policyInfoFilterCount,
    giftFilterCount,
    donationFilterCount,
    festivalFilterCount
  } = useIntegratedSearchFilter();
  const searchHistory = useSearchHistoryStore(_temp);
  const add = useSearchHistoryStore(_temp2);
  const remove = useSearchHistoryStore(_temp3);
  const clear = useSearchHistoryStore(_temp4);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    ref: searchBarRef
  } = usePageSearchBarVisibility();
  let t0;
  if ($[0] !== searchParams) {
    const categoryParam = searchParams.get(SEARCH_CATEGORY_QUERY_KEY);
    t0 = isSearchCategory(categoryParam) ? categoryParam : DEFAULT_SEARCH_CATEGORY;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const category = t0;
  const isAllCategory = category === "all";
  const isPolicyCategory = category === "policy";
  let t1;
  if ($[2] !== add || $[3] !== category || $[4] !== setKeyword) {
    t1 = keyword => {
      setKeyword(keyword);
      add(keyword);
      if (keyword && category !== "all") {
        trackGtmEvent(GTM_EVENT_TYPE.SEARCH, {
          search_type: "\uD1B5\uD569\uAC80\uC0C9",
          search_term: keyword,
          search_category: category
        });
      }
    };
    $[2] = add;
    $[3] = category;
    $[4] = setKeyword;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleSearch = t1;
  let t2;
  if ($[6] !== category || $[7] !== donationFilterCount || $[8] !== festivalFilterCount || $[9] !== giftFilterCount || $[10] !== hometownNewsFilterCount || $[11] !== policyInfoFilterCount) {
    const getIntegratedFilterCount = function getIntegratedFilterCount() {
      switch (category) {
        case "hometown-news":
          {
            return hometownNewsFilterCount;
          }
        case "policy-info":
          {
            return policyInfoFilterCount;
          }
        case "gifts":
          {
            return giftFilterCount;
          }
        case "hometown-business":
          {
            return donationFilterCount;
          }
        case "festival":
          {
            return festivalFilterCount;
          }
        default:
          {
            return 0;
          }
      }
    };
    t2 = getIntegratedFilterCount();
    $[6] = category;
    $[7] = donationFilterCount;
    $[8] = festivalFilterCount;
    $[9] = giftFilterCount;
    $[10] = hometownNewsFilterCount;
    $[11] = policyInfoFilterCount;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const integratedFilterCount = t2;
  const hasIntegratedFilter = category === "hometown-news" || category === "policy-info" || category === "gifts" || category === "hometown-business" || category === "festival";
  const filterCount = isAllCategory ? 0 : isPolicyCategory ? activeFilterCount : integratedFilterCount;
  let t3;
  if ($[13] !== hasIntegratedFilter || $[14] !== isAllCategory || $[15] !== isPolicyCategory) {
    t3 = !isAllCategory && (isPolicyCategory || hasIntegratedFilter) ? () => setIsFilterOpen(true) : undefined;
    $[13] = hasIntegratedFilter;
    $[14] = isAllCategory;
    $[15] = isPolicyCategory;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const onFilterClick = t3;
  let t4;
  if ($[17] !== isDesktopViewport) {
    t4 = () => {
      const resultsSection = resultsSectionRef.current;
      if (!resultsSection) {
        return;
      }
      const top = resultsSection.getBoundingClientRect().top + window.scrollY - (isDesktopViewport ? 24 : 56);
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: "auto"
      });
    };
    $[17] = isDesktopViewport;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const scrollToResultsTop = t4;
  let t5;
  if ($[19] !== scrollToResultsTop || $[20] !== setPage) {
    t5 = page => {
      setPage(page);
      scrollToResultsTop();
    };
    $[19] = scrollToResultsTop;
    $[20] = setPage;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const handlePageChange = t5;
  let t6;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsFilterOpen(false);
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const closeFilter = t6;
  let t7;
  if ($[23] !== category || $[24] !== handlePageChange || $[25] !== integratedState || $[26] !== isFilterOpen || $[27] !== resetDonationFilters || $[28] !== resetFestivalFilters || $[29] !== resetFilters || $[30] !== resetGiftFilters || $[31] !== resetHometownNewsFilters || $[32] !== resetPolicyInfoFilters || $[33] !== setDonationSort || $[34] !== setFestivalLclsSystm2 || $[35] !== setFestivalMonth || $[36] !== setFestivalRadius || $[37] !== setFestivalRegionCode || $[38] !== setFestivalSubRegionCode || $[39] !== setRegionCode || $[40] !== setSort || $[41] !== setSubRegionCode || $[42] !== state || $[43] !== toggleAgeCode || $[44] !== toggleDonationInstitution || $[45] !== toggleGiftCategory || $[46] !== toggleGiftInstitution || $[47] !== toggleJobCode || $[48] !== togglePolicyInfoProvision || $[49] !== toggleProvisionCode || $[50] !== toggleTargetCode) {
    t7 = () => {
      switch (category) {
        case "all":
          {
            return <AllSearch keyword={state.keyword} />;
          }
        case "policy":
          {
            return <PolicySearch isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} state={state} onToggleTargetCode={toggleTargetCode} onToggleProvisionCode={toggleProvisionCode} onToggleJobCode={toggleJobCode} onToggleAgeCode={toggleAgeCode} onResetFilters={resetFilters} onSortChange={setSort} onPageChange={handlePageChange} />;
          }
        case "hometown-news":
          {
            return <HometownNewsSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} regionCode={integratedState.regionCode} subRegionCode={integratedState.subRegionCode} onRegionChange={setRegionCode} onSubRegionChange={setSubRegionCode} onReset={resetHometownNewsFilters} />;
          }
        case "festival":
          {
            return <FestivalSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} lclsSystm2={integratedState.festivalLclsSystm2} onSelectLclsSystm2={setFestivalLclsSystm2} regionCode={integratedState.festivalRegionCode} subRegionCode={integratedState.festivalSubRegionCode} onSelectRegion={setFestivalRegionCode} onSelectSubRegion={setFestivalSubRegionCode} radius={integratedState.festivalRadius} onSelectRadius={setFestivalRadius} month={integratedState.festivalMonth} onSelectMonth={setFestivalMonth} onReset={resetFestivalFilters} />;
          }
        case "policy-info":
          {
            return <PolicyInfoSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} provisionCodes={integratedState.policyInfoProvisionCodes} onToggleProvision={togglePolicyInfoProvision} onReset={resetPolicyInfoFilters} />;
          }
        case "gifts":
          {
            return <GiftSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} categoryIds={integratedState.giftCategoryIds} institutionIds={integratedState.giftInstitutionIds} onToggleCategory={toggleGiftCategory} onToggleInstitution={toggleGiftInstitution} onReset={resetGiftFilters} />;
          }
        case "hometown-business":
          {
            return <DonationBusinessSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} institutionIds={integratedState.donationInstitutionIds} sort={integratedState.donationSort} onToggleInstitution={toggleDonationInstitution} onSortChange={setDonationSort} onReset={resetDonationFilters} />;
          }
        case "welmarket":
          {
            return <LocalMarketSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} />;
          }
      }
    };
    $[23] = category;
    $[24] = handlePageChange;
    $[25] = integratedState;
    $[26] = isFilterOpen;
    $[27] = resetDonationFilters;
    $[28] = resetFestivalFilters;
    $[29] = resetFilters;
    $[30] = resetGiftFilters;
    $[31] = resetHometownNewsFilters;
    $[32] = resetPolicyInfoFilters;
    $[33] = setDonationSort;
    $[34] = setFestivalLclsSystm2;
    $[35] = setFestivalMonth;
    $[36] = setFestivalRadius;
    $[37] = setFestivalRegionCode;
    $[38] = setFestivalSubRegionCode;
    $[39] = setRegionCode;
    $[40] = setSort;
    $[41] = setSubRegionCode;
    $[42] = state;
    $[43] = toggleAgeCode;
    $[44] = toggleDonationInstitution;
    $[45] = toggleGiftCategory;
    $[46] = toggleGiftInstitution;
    $[47] = toggleJobCode;
    $[48] = togglePolicyInfoProvision;
    $[49] = toggleProvisionCode;
    $[50] = toggleTargetCode;
    $[51] = t7;
  } else {
    t7 = $[51];
  }
  const renderSection = t7;
  const t8 = SECTION_HEADER_MAX_W[category];
  let t9;
  if ($[52] !== t8) {
    t9 = cn("w-full max-w-screen-xl", t8);
    $[52] = t8;
    $[53] = t9;
  } else {
    t9 = $[53];
  }
  let t10;
  if ($[54] !== searchHistory) {
    t10 = searchHistory ?? [];
    $[54] = searchHistory;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  let t11;
  if ($[56] !== clear || $[57] !== filterCount || $[58] !== handleSearch || $[59] !== onFilterClick || $[60] !== remove || $[61] !== searchBarRef || $[62] !== state.keyword || $[63] !== t10) {
    t11 = <SearchPageHeader ref={searchBarRef} keyword={state.keyword} filterCount={filterCount} recentKeywords={t10} onSearch={handleSearch} onFilterClick={onFilterClick} onSelectRecent={handleSearch} onRemoveRecent={remove} onClearRecent={clear} />;
    $[56] = clear;
    $[57] = filterCount;
    $[58] = handleSearch;
    $[59] = onFilterClick;
    $[60] = remove;
    $[61] = searchBarRef;
    $[62] = state.keyword;
    $[63] = t10;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  let t12;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="mt-12 mobile-tablet:mt-0 mobile-tablet:px-16 mobile-tablet:py-12"><SearchTopAdBanner /></div>;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  let t13;
  if ($[66] !== t11 || $[67] !== t9) {
    t13 = <div className={t9}>{t11}{t12}</div>;
    $[66] = t11;
    $[67] = t9;
    $[68] = t13;
  } else {
    t13 = $[68];
  }
  let t14;
  if ($[69] !== renderSection) {
    t14 = renderSection();
    $[69] = renderSection;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  let t15;
  if ($[71] !== t14) {
    t15 = <div ref={resultsSectionRef} className="w-full">{t14}</div>;
    $[71] = t14;
    $[72] = t15;
  } else {
    t15 = $[72];
  }
  let t16;
  if ($[73] !== t13 || $[74] !== t15) {
    t16 = <div className="flex w-full flex-col items-center gap-12 p-40 mobile-tablet:gap-0 mobile-tablet:p-0">{t13}{t15}</div>;
    $[73] = t13;
    $[74] = t15;
    $[75] = t16;
  } else {
    t16 = $[75];
  }
  return t16;
}
function _temp4(state_3) {
  return state_3.clear;
}
function _temp3(state_2) {
  return state_2.remove;
}
function _temp2(state_1) {
  return state_1.add;
}
function _temp(state_0) {
  return state_0.data;
}